import { Edges, TransformControls } from '@react-three/drei';
import { useCallback, useEffect, useRef } from 'react';
import { Euler, Vector3 } from 'three';

import { StateNames, States } from '../../Screens/Configurator';
import { GranitMaterial } from './GranitMaterial';

type BoxCustomProps = {
  customBox: {
    id: string;
    key: string;
    shape: any;
    shapeDetails: any;
    position: Vector3;
    texture: THREE.Texture;
    isBlocked: boolean;
    extrudeSettings: any;
  };
  orbitRef: React.MutableRefObject<any>;
  pieceIndex: number;
  setPieceIndex: (pieceIndex: number) => void;
  index: number;
  setCustomBoxes: (customBoxes: any[]) => void;
  customBoxes: any[];
  getValue: (stateName: StateNames) => States[StateNames];
  step: string;
};

export const BoxCustom = ({
  customBox,
  orbitRef,
  pieceIndex,
  setPieceIndex,
  index,
  setCustomBoxes,
  customBoxes,
  getValue,
  step,
}: BoxCustomProps) => {
  const transformRef = useRef<any>();
  const meshRef = useRef<any>();

  useEffect(() => {
    if (transformRef.current) {
      if (meshRef.current) transformRef.current.attach(meshRef.current);
      const controls = transformRef.current;
      const callback = (event: any) => {
        orbitRef.current.enabled = !event.value;
      };
      controls.addEventListener('dragging-changed', callback);
      return () => controls.removeEventListener('dragging-changed', callback);
    }
  });

  const handleChangePiecePosition = useCallback(() => {
    if (index === pieceIndex) {
      const deltaPosition = new Vector3(
        (meshRef?.current?.position.x ?? 0) - customBox.position.x,
        (meshRef?.current?.position.y ?? 0) - customBox.position.y,
        (meshRef?.current?.position.z ?? 0) - customBox.position.z,
      );

      const customsMeshTmp = customBoxes.map((mesh) => {
        const newPosition = new Vector3(
          +mesh.position.x + deltaPosition.x,
          +mesh.position.y + deltaPosition.y,
          +mesh.position.z + deltaPosition.z,
        );

        return {
          ...mesh,
          position: newPosition,
        };
      });

      setCustomBoxes(customsMeshTmp);
    }
  }, [
    index,
    pieceIndex,
    customBox.position.x,
    customBox.position.y,
    customBox.position.z,
    customBoxes,
    setCustomBoxes,
  ]);

  return (
    <TransformControls
      ref={transformRef}
      mode="translate"
      size={index === pieceIndex && !customBox.isBlocked ? 1 : 0}
      onMouseUp={() => handleChangePiecePosition()}
    >
      <mesh
        ref={meshRef}
        position={customBox.position}
        rotation={new Euler(0, customBox.shapeDetails.isRotated ? 1.57 : 0, 0, 'XYZ')}
        onClick={(e) => {
          if (e.intersections && e.intersections[0].distance === e.distance) {
            setPieceIndex(pieceIndex);
          }
        }}
      >
        <extrudeGeometry attach="geometry" args={[customBox.shape, customBox.extrudeSettings]} />
        {getValue('texture') ? (
          <GranitMaterial map={customBox.texture} metalness={0.2} />
        ) : (
          <>
            <Edges />
            <meshStandardMaterial />
          </>
        )}
        {index === pieceIndex &&
        (step === 'CUSTOMIZATION' || step === 'ACCESSORIES' || step === 'PATTERNS') ? (
          <Edges color="red" threshold={10} />
        ) : null}
      </mesh>
    </TransformControls>
  );
};
